<template>
  <section id="supply-offer-tariffs">
    <header>
      Тарифы
      <VLink material="add" @click="showTariffCard()"> Добавить тариф </VLink>
    </header>

    <VDataGridV2
      class="supply-offer-tariffs-grid fill"
      :data="supplyOffer.sortedTariffs"
      @row:dblclick="(row, index) => showTariffCard(row, index)"
    >
      <VDataGridColumnV2
        id="badge"
        width="34px"
      >
        <template #default="{ row }">
          <VBadge
            v-if="isShowBudgetPriceBadge(row)"
            class="success"
            @mouseenter="$core.tooltip.show($event.currentTarget, 'Бюджетная цена')"
            @mouseleave="$core.tooltip.hide()"
          > Б
          </VBadge>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="price"
        label="Цена"
        width="170px"
        content-align="right"
        content-class="ellipsis"
      >
        <template #default="{ row }">
          <div>{{ row.price.formatInDisplayCurrency(row.currencyCode, DateSimple.today()) }}</div>
          <div
            v-if="row.currencyCode !== $core.utils.money.displayCurrency?.code"
            class="description"
          >
            {{ row.price.format(row.currency) }}
          </div>
          <div
            v-if="row.isFromOrder"
            class="description"
          >
            <VLink @click="OrdersService.showOrderPositionCardById(row.orderPositionId)">из заказа №{{ row.orderId }}</VLink>
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="qty"
        label="Кол-во"
        width="150px"
        content-align="right"
        content-class="ellipsis"
      >
        <template #default="{ row }">
          от {{ row.qty?.start ?? '--' }} {{ supplyOffer.unit }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2 id="date" label="Действует" width="190px">
        <template #default="{ row }">
          <div
            class="grey"
            :class="{ error: row.isExpired || row.isFinished }"
            @mouseenter="onMouseEnter(row, $event)"
            @mouseleave="onMouseLeave($event)"
          >
            {{ row.validityPeriod.toString() }}
          </div>
          <div
            v-if="row.isFuture"
            class="description"
          >
            ещё не действует, в прайсе не показывается
          </div>
          <div
            v-else-if="row.isFinished"
            class="description"
          >
            срок действия цены истёк
          </div>
        </template>
      </VDataGridColumnV2>
    </VDataGridV2>
  </section>
</template>

<style lang="scss" src="./SupplyOfferTariffsGrid.scss" />

<script lang="ts">
import VBadgeIcon from '@/lib/components/VBadgeIcon/VBadgeIcon.vue';

import SupplyOffer         from '@/domains/SupplyOffers/SupplyOffer';
import SupplyOfferTariff   from '@/domains/SupplyOffers/SupplyOfferTariff';
import SupplyOffersService from '@/domains/SupplyOffers/SupplyOffersService';
import BudgetPrice         from '@/domains/BudgetPrices/BudgetPrice';
import OrdersService       from '@/domains/Orders/OrdersService';
import DateSimple          from '@/core/types/DateSimple';

export default defineComponent({
  name: 'SupplyOfferTariffsGrid',

  components: {
    VBadgeIcon,
  },

  props: {
    supplyOffer: {
      type:     Object as PropType<SupplyOffer>,
      required: true,
    },
    budgetPrice: {
      type:    Object as PropType<BudgetPrice | null>,
      default: null,
    },
  },

  emits: [
    'tariff-created',
  ],

  setup() {
    return { SupplyOffersService, OrdersService, DateSimple };
  },

  methods: {
    /**
     * Бюджетная цена пересчитывается в валюту каждого тарифа по текущему курсу.
     * Курс читается из реактивного кеша: пока он едет, бейджа нет, после загрузки шаблон перерисуется сам
     */
    isShowBudgetPriceBadge(row: SupplyOfferTariff): boolean {
      let result: boolean;

      if (this.$core.utils.isNullOrUndefined(this.budgetPrice?.price) === true) {
        result = false;
      }
      else {
        const money         = this.$core.utils.money;
        const today         = DateSimple.today();
        const budgetPrice   = this.budgetPrice!;
        const fallbackMark  = money.getRateFallbackMark(budgetPrice.currency!, row.currencyCode, today);

        if (fallbackMark !== null) {
          result = false;
        }
        else {
          result = row.isLessOrEqualBudgetPrice(money.convertSync(budgetPrice.price, budgetPrice.currency!, row.currencyCode, today));
        }
      }

      return result;
    },

    onMouseEnter(row, $event) {
      if (row.isExpired) {
        this.$core.tooltip.show(
          $event.currentTarget, `Цене больше ${SupplyOffersService.PRICE_EXPIRATION_DAYS} дней`
        );
      }
    },

    onMouseLeave() {
      this.$core.tooltip.hide();
    },

    showTariffCard(tariff = undefined): void {
      this.$core.dialogs.SupplyOfferTariffCard.show(
        {
          supplyOffer: this.supplyOffer,
          tariff:      tariff,
        },
        {
          [SupplyOffer.Events.Updated]: (createdTariff) => {
            this.supplyOffer.putTariff(createdTariff, tariff ?? null);
            this.$emit('tariff-created');
          },
        },
      );
    },
  },
});
</script>
